import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authApi, marketplaceApi } from "../api/client";
import LogoMark from "../components/LogoMark";

const accountTypes = [
  {
    value: "BUYER",
    title: "Buyer",
    text: "Source products, place enquiries and select delivery support.",
  },
  {
    value: "SELLER",
    title: "Seller",
    text: "List products, manage enquiries, documents and marketplace activity.",
  },
  {
    value: "COURIER",
    title: "Transporter",
    text: "Register services, coverage areas and delivery participation.",
  },
  {
    value: "BUSINESS",
    title: "Business",
    text: "Access merchant inventory, movement tracking and subscription tools.",
  },
];

export default function Register() {
  const [form, setForm] = useState({
    account_type: "BUYER",
    email: "",
    phone: "",
    password: "",
    first_name: "",
    last_name: "",
    national_id: "",
    business_name: "",
    business_registration_number: "",
    tax_identification_number: "",
    business_category: "",
    category_ids: [],
    years_operating: "",
    address: "",
    city: "",
    province: "",
  });
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const nextPath = params.get("next") || "/portal";
  const requestedType = (params.get("type") || "").toUpperCase();

  useEffect(() => {
    if (["BUYER", "SELLER", "COURIER", "BUSINESS"].includes(requestedType)) {
      update("account_type", requestedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedType]);

  useEffect(() => {
    let mounted = true;
    marketplaceApi.categories()
      .then((res) => {
        if (mounted) setCategories(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (mounted) setCategories([]);
      });
    return () => { mounted = false; };
  }, []);

  function update(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function toggleCategory(category) {
    setForm((prev) => {
      const exists = prev.category_ids.includes(category.id);
      const nextIds = exists ? prev.category_ids.filter((id) => id !== category.id) : [...prev.category_ids, category.id];
      const names = categories.filter((item) => nextIds.includes(item.id)).map((item) => item.name).join(", ");
      return { ...prev, category_ids: nextIds, business_category: names };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    const payload = { ...form, country: "ZIMBABWE" };
    if (!payload.years_operating) delete payload.years_operating;

    try {
      if (!payload.category_ids?.length) delete payload.category_ids;
      await authApi.register(payload);
      setSuccess("Account created. Sign in to continue. Buyers can complete their purchase flow immediately, while sellers, businesses and transporters continue through verification.");
      setTimeout(() => navigate(`/login?next=${encodeURIComponent(nextPath)}`), 1300);
    } catch (err) {
      const data = err?.response?.data;
      const firstMessage = data && typeof data === "object"
        ? Object.values(data).flat().join(" ")
        : "Registration failed. Check required fields or try a different email/phone.";
      setError(firstMessage || "Registration failed. Check required fields or try again.");
    }
  }

  const selectedAccount = accountTypes.find((type) => type.value === form.account_type);
  const needsTradingInfo = form.account_type !== "BUYER";
  const needsProductCategory = ["SELLER", "BUSINESS"].includes(form.account_type);

  return (
    <div className="auth-page-v2 refined-login-page refined-register-page">
      <div className="auth-orb auth-orb--one" />
      <div className="auth-orb auth-orb--two" />

      <header className="auth-header-v2 refined-login-header">
        <Link to="/" aria-label="TAZY CART marketplace">
          <LogoMark />
        </Link>
        <nav className="auth-header-links">
          <Link to="/">Marketplace</Link>
          <Link to={`/login?next=${encodeURIComponent(nextPath)}`}>Sign in</Link>
        </nav>
      </header>

      <main className="auth-stage-v2 refined-login-stage register-stage-v2">
        <section className="auth-visual-panel refined-login-copy register-copy-v2">
          <span className="auth-kicker">CREATE MARKETPLACE ACCESS</span>
          <h1>Choose your lane and join TAZY CART.</h1>
          <p>
            Register as a buyer, seller, transporter or business account. Seller and business
            applications now capture the product category focus so approvals are meaningful from day one.
          </p>

          <div className="register-trust-grid">
            <article>
              <span>01</span>
              <strong>Verified participation</strong>
              <small>Seller, transporter and business accounts enter a controlled approval workflow.</small>
            </article>
            <article>
              <span>02</span>
              <strong>Product category review</strong>
              <small>TAZY can see what sellers plan to trade in before opening full marketplace access.</small>
            </article>
            <article>
              <span>03</span>
              <strong>Delivery network</strong>
              <small>Transporters can be approved into the logistics layer of the platform.</small>
            </article>
          </div>
        </section>

        <section className="register-card-v2" aria-label="Create TAZY CART account">
          <div className="register-card-head-v2">
            <img src="/tazy-logo.jpeg" alt="TAZY CART" />
            <div>
              <span className="api-pill api-pill--ok">Secure registration</span>
              <h2>Create account</h2>
              <p>{selectedAccount?.title} access selected</p>
            </div>
          </div>

          {error && <div className="error-box">{error}</div>}
          {success && <div className="success-box">{success}</div>}

          <div className="register-scroll-area">
            <div className="account-type-grid-v2">
              {accountTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  className={form.account_type === type.value ? "selected" : ""}
                  onClick={() => update("account_type", type.value)}
                >
                  <strong>{type.title}</strong>
                  <span>{type.text}</span>
                </button>
              ))}
            </div>

            <form className="register-form-v2" onSubmit={handleSubmit}>
              <div className="form-row">
                <div>
                  <label>First name</label>
                  <input value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required />
                </div>
                <div>
                  <label>Last name</label>
                  <input value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required />
                </div>
              </div>

              <label>Email address</label>
              <input value={form.email} onChange={(e) => update("email", e.target.value)} type="email" required />

              <label>Phone number</label>
              <input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+263..." />

              {needsTradingInfo && (
                <>
                  <label>National ID number</label>
                  <input
                    value={form.national_id}
                    onChange={(e) => update("national_id", e.target.value)}
                    placeholder="Example: 63-123456-A-12"
                    required
                  />
                  <small className="soft-text">Required for identity verification. You will upload the matching National ID document after signing in.</small>
                </>
              )}

              {needsTradingInfo && (
                <>
                  <label>{form.account_type === "COURIER" ? "Transporter / trading name" : "Business / trading name"}</label>
                  <input value={form.business_name} onChange={(e) => update("business_name", e.target.value)} placeholder="Registered or trading name" required />
                </>
              )}

              {needsProductCategory && (
                <>
                  <label>Product categories you intend to sell</label>
                  <div className="registration-category-picker">
                    {categories.map((category) => (
                      <button
                        type="button"
                        key={category.id || category.code}
                        className={form.category_ids.includes(category.id) ? "selected" : ""}
                        onClick={() => toggleCategory(category)}
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                  <input type="hidden" value={form.business_category} required readOnly />
                </>
              )}

              {needsTradingInfo && (
                <div className="form-row">
                  <div>
                    <label>Company registration number</label>
                    <input value={form.business_registration_number} onChange={(e) => update("business_registration_number", e.target.value)} placeholder="Registered companies only" />
                    <small className="soft-text">If supplied, the company registration document must be approved before the account can be verified.</small>
                  </div>
                  <div>
                    <label>Years operating</label>
                    <input value={form.years_operating} onChange={(e) => update("years_operating", e.target.value)} type="number" min="0" placeholder="0" />
                  </div>
                </div>
              )}

              <label>Physical address</label>
              <input value={form.address} onChange={(e) => update("address", e.target.value)} placeholder="Shop, office or residential address" />

              <div className="form-row">
                <div>
                  <label>City</label>
                  <input value={form.city} onChange={(e) => update("city", e.target.value)} />
                </div>
                <div>
                  <label>Province</label>
                  <input value={form.province} onChange={(e) => update("province", e.target.value)} />
                </div>
              </div>

              <label>Password</label>
              <input value={form.password} onChange={(e) => update("password", e.target.value)} type="password" minLength="6" required />

              <button type="submit">Create account</button>
            </form>
            <div className="register-card-footer-v2 auth-help-links">
              <span>Already have an account?</span>
              <Link to={`/login?next=${encodeURIComponent(nextPath)}`}>Sign in</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="auth-footer-v2 refined-login-footer">
        <span>© {new Date().getFullYear()} TAZY CART Marketplace</span>
        <span>Developed by <a href="https://bronixtechnologies.com" target="_blank" rel="noreferrer">Bronix Technologies</a></span>
      </footer>
    </div>
  );
}
